import React from 'react';
import { motion } from 'framer-motion';
import {
  GitBranch,
  Wallet,
  Users,
  FileBarChart,
  LayoutDashboard,
  MessageCircle,
  TrendingUp,
  ChevronRight,
  Bell,
  Search,
  HandCoins,
  Car,
} from 'lucide-react';

function BrowserChrome({ title, accent }: { title: string; accent: string }) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.08] bg-white/[0.02] px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
        </div>
        <div className="hidden items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-1 sm:flex">
          <Search className="h-3 w-3 text-slate-500" />
          <span className="text-[10px] text-slate-500">app.edgefinances.com</span>
        </div>
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${accent}`}>{title}</span>
    </div>
  );
}

function Sidebar({ active }: { active: string }) {
  const items = [
    { icon: LayoutDashboard, label: 'Master', id: 'master' },
    { icon: Wallet, label: 'Finance', id: 'finance' },
    { icon: HandCoins, label: 'Hand loans', id: 'hand' },
    { icon: Users, label: 'Customers', id: 'customers' },
    { icon: FileBarChart, label: 'Reports', id: 'reports' },
    { icon: GitBranch, label: 'Ledgers', id: 'ledgers' },
  ];
  return (
    <div className="hidden w-14 shrink-0 flex-col gap-1 border-r border-white/[0.06] bg-black/20 py-4 sm:flex lg:w-16">
      {items.map((item) => (
        <div
          key={item.id}
          className={`mx-2 flex flex-col items-center gap-0.5 rounded-xl py-2 ${
            active === item.id ? 'bg-emerald-500/15 text-emerald-300' : 'text-slate-500'
          }`}
        >
          <item.icon className="h-4 w-4" />
          <span className="text-[7px] font-medium">{item.label.split(' ')[0]}</span>
        </div>
      ))}
    </div>
  );
}

function StatPill({ label, value, trend }: { label: string; value: string; trend?: string }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-white/[0.06] to-transparent p-3">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 font-display text-lg font-bold text-white">{value}</p>
      {trend && <p className="mt-0.5 text-[10px] font-medium text-emerald-400">{trend}</p>}
    </div>
  );
}

export function LedgerScreenMockup() {
  const branches = [
    { name: 'HQ · Main book', city: 'Head office', active: true, collections: '₹18.4L' },
    { name: 'Hyderabad branch', city: 'Madhapur', active: false, collections: '₹12.1L' },
    { name: 'Vijayawada branch', city: 'Benz Circle', active: false, collections: '₹9.6L' },
    { name: 'Warangal branch', city: 'Hanamkonda', active: false, collections: '₹6.2L' },
  ];

  return (
    <MockupFrame accent="text-violet-300" title="Branch ledgers">
      <BrowserChrome title="Ledger switcher" accent="text-violet-300" />
      <div className="flex min-h-[280px] sm:min-h-[320px]">
        <Sidebar active="ledgers" />
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-400">Multi-branch</p>
              <h4 className="mt-1 font-display text-base font-semibold text-white sm:text-lg">Your organization books</h4>
            </div>
            <button type="button" className="rounded-lg bg-violet-500/20 px-3 py-1.5 text-[10px] font-semibold text-violet-200">
              + New ledger
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {branches.map((b) => (
              <motion.div
                key={b.name}
                whileHover={{ x: 4 }}
                className={`flex items-center justify-between rounded-xl border px-3 py-3 sm:px-4 ${
                  b.active
                    ? 'border-violet-500/40 bg-violet-500/10 shadow-[0_0_24px_-8px_rgba(139,92,246,0.5)]'
                    : 'border-white/[0.06] bg-white/[0.02]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${b.active ? 'bg-violet-500/30' : 'bg-white/5'}`}>
                    <GitBranch className={`h-4 w-4 ${b.active ? 'text-violet-200' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-white sm:text-sm">{b.name}</p>
                    <p className="text-[10px] text-slate-500">{b.city}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-emerald-300">{b.collections}</p>
                  <p className="text-[9px] text-slate-500">this month</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

export function FinanceDeskMockup() {
  return (
    <MockupFrame accent="text-emerald-300" title="Finance desk">
      <BrowserChrome title="HP origination" accent="text-emerald-300" />
      <div className="flex min-h-[280px] sm:min-h-[320px]">
        <Sidebar active="finance" />
        <div className="flex-1 p-4 sm:p-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatPill label="Active HPs" value="284" trend="↑ 12 this week" />
            <StatPill label="Disbursed" value="₹4.2Cr" />
            <StatPill label="EMI today" value="₹8.4L" trend="94% collected" />
            <StatPill label="Seized" value="3" />
          </div>
          <div className="mt-4 rounded-xl border border-white/[0.08] bg-black/20">
            <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
              <span className="text-[10px] font-semibold text-slate-400">Recent facilities</span>
              <ChevronRight className="h-3 w-3 text-slate-600" />
            </div>
            {[
              { id: 'HP-2847', borrower: 'Ramesh K.', amount: '₹4.2L', status: 'Active', color: 'text-emerald-400' },
              { id: 'HP-2846', borrower: 'Suresh P.', amount: '₹3.8L', status: 'EMI due', color: 'text-amber-400' },
              { id: 'HP-2845', borrower: 'Lakshmi D.', amount: '₹5.1L', status: 'Closed', color: 'text-slate-500' },
            ].map((row) => (
              <div key={row.id} className="flex items-center justify-between border-b border-white/[0.04] px-3 py-2.5 last:border-0">
                <div className="flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 text-emerald-400" />
                  <div>
                    <p className="text-[11px] font-semibold text-white">{row.borrower}</p>
                    <p className="text-[9px] text-slate-500">{row.id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-bold text-white">{row.amount}</p>
                  <p className={`text-[9px] font-medium ${row.color}`}>{row.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

export function CustomerProfileMockup() {
  return (
    <MockupFrame accent="text-cyan-300" title="Customer 360">
      <BrowserChrome title="Borrower profile" accent="text-cyan-300" />
      <div className="flex min-h-[280px] sm:min-h-[320px]">
        <Sidebar active="customers" />
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500/40 to-blue-600/30 text-sm font-bold text-white ring-2 ring-cyan-500/30">
              RK
            </div>
            <div className="flex-1">
              <p className="font-display text-sm font-semibold text-white sm:text-base">Ramesh Kumar</p>
              <p className="text-[10px] text-slate-500">2 facilities · Hyderabad branch</p>
            </div>
            <button type="button" className="flex items-center gap-1 rounded-lg bg-emerald-500/20 px-2.5 py-1.5 text-[10px] font-semibold text-emerald-200">
              <MessageCircle className="h-3 w-3" />
              WhatsApp
            </button>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <StatPill label="Outstanding" value="₹1.8L" />
            <StatPill label="Overdue" value="4 days" trend="View dates" />
            <StatPill label="Next EMI" value="₹12,400" />
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2">
            <Bell className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[10px] text-amber-100">Reminder sent · EMI due in 2 days</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" className="rounded-lg border border-white/10 bg-white/[0.04] py-2 text-[10px] font-semibold text-white">
              Record payment
            </button>
            <button type="button" className="rounded-lg border border-white/10 bg-white/[0.04] py-2 text-[10px] font-semibold text-white">
              Export profile
            </button>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

export function ReportsMockup() {
  const bars = [42, 68, 55, 82, 74, 91, 78];
  return (
    <MockupFrame accent="text-amber-300" title="Day reports">
      <BrowserChrome title="Period analytics" accent="text-amber-300" />
      <div className="flex min-h-[280px] sm:min-h-[320px]">
        <Sidebar active="reports" />
        <div className="flex-1 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400">July 2026</p>
            <span className="rounded-full border border-white/10 px-2 py-0.5 text-[9px] text-slate-400">Export PDF</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { l: 'Created', v: '12' },
              { l: 'Closed', v: '8' },
              { l: 'Collections', v: '₹8.4L' },
              { l: 'Outstanding', v: '₹42L' },
            ].map((s) => (
              <StatPill key={s.l} label={s.l} value={s.v} />
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-white/[0.08] bg-black/20 p-3">
            <p className="mb-2 text-[10px] font-semibold text-slate-400">Collections trend</p>
            <div className="flex h-20 items-end gap-1">
              {bars.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t bg-gradient-to-t from-amber-600 to-amber-400"
                  style={{ height: `${h * 0.7}%` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

export function MasterDashboardMockup() {
  return (
    <MockupFrame accent="text-emerald-300" title="Command center">
      <BrowserChrome title="Owner dashboard" accent="text-emerald-300" />
      <div className="flex min-h-[280px] sm:min-h-[320px]">
        <Sidebar active="master" />
        <div className="flex-1 p-4 sm:p-5">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <StatPill label="Net position" value="₹28.4L" trend="↑ 14% MoM" />
            <StatPill label="Borrowers" value="1,247" />
            <StatPill label="Collected" value="₹42.8L" />
            <StatPill label="Consultancy" value="₹3.2L" />
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-white/[0.08] bg-black/20 p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                <span className="text-[10px] font-semibold text-slate-300">Portfolio split</span>
              </div>
              <div className="mt-3 flex h-2 overflow-hidden rounded-full">
                <div className="w-[62%] bg-emerald-500" />
                <div className="w-[28%] bg-cyan-500" />
                <div className="w-[10%] bg-violet-500" />
              </div>
              <div className="mt-2 flex justify-between text-[9px] text-slate-500">
                <span>HP 62%</span>
                <span>Hand 28%</span>
                <span>Other</span>
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-black/20 p-3">
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4 text-cyan-400" />
                <span className="text-[10px] font-semibold text-slate-300">Top concentration</span>
              </div>
              <p className="mt-2 font-display text-lg font-bold text-white">Hyderabad</p>
              <p className="text-[10px] text-emerald-400">₹18.4L · 43% of collections</p>
            </div>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
}

function MockupFrame({
  children,
  title,
  accent,
}: {
  children: React.ReactNode;
  title: string;
  accent: string;
}) {
  return (
    <div className="ef-launch-device relative">
      <div className="ef-launch-device__glow" aria-hidden />
      <motion.div
        className="ef-launch-device__screen"
        initial={{ opacity: 0, y: 24, rotateX: 8 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="ef-launch-device__bezel">
          <div className="overflow-hidden rounded-[1.1rem] border border-white/[0.1] bg-[#0a0f1a] shadow-2xl">
            {children}
          </div>
        </div>
        <p className={`mt-4 text-center text-[10px] font-bold uppercase tracking-[0.25em] ${accent}`}>{title}</p>
      </motion.div>
    </div>
  );
}
